/* src/components/common/SupportWidget.js */
import React, { useState, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
    XIcon, 
    PaperAirplaneIcon, 
    SparklesIcon 
} from '../icons/Icons'; 

export default function SupportWidget({ userProfile }) {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { 
            role: 'model', 
            text: `Hi ${userProfile?.name?.split(' ')[0] || 'there'}! I'm your LINC Support Assistant. How can I help you today?` 
        }
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    
    // Refs for scrolling and focus
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-scroll to bottom
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isOpen]);

    // Auto-focus when opening
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        const userMsg = input.trim();
        setInput('');
        
        // 1. Add User Message
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setIsTyping(true);

        // 2. IMMEDIATE FOCUS BACK TO INPUT
        setTimeout(() => {
            inputRef.current?.focus();
        }, 10);

        try {
            const historyForBackend = messages.map(m => ({
                role: m.role,
                parts: [{ text: m.text }]
            }));

            const functions = getFunctions();
            const chatFn = httpsCallable(functions, 'chatWithSupport');
            
            const result = await chatFn({
                userMessage: userMsg,
                history: historyForBackend,
                appId: "admin-c78c6"
            });

            setMessages(prev => [...prev, { role: 'model', text: result.data.responseText }]);

        } catch (error) {
            console.error("Support Chat Error:", error);
            setMessages(prev => [...prev, { role: 'model', text: "I'm having trouble connecting right now. Please try again later." }]);
        } finally {
            setIsTyping(false);
            // Ensure focus is kept even after loading
            setTimeout(() => {
                inputRef.current?.focus();
            }, 10);
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end font-sans">
            
            {/* CHAT WINDOW */}
            {isOpen && (
                <div className="mb-4 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col h-[500px] animate-fadeIn">
                    
                    {/* Header */}
                    <div className="bg-[#213368] p-4 flex justify-between items-center text-white shadow-md">
                        <div className="flex items-center space-x-2">
                            <div className="p-1.5 bg-white/10 rounded-full">
                                <SparklesIcon className="h-5 w-5 text-[#20BEC6]" />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm">LINC Support</h3>
                                <p className="text-[10px] text-blue-200">Powered by Gemini AI</p>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded transition">
                            <XIcon className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" ref={scrollRef}>
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] p-3 rounded-2xl text-sm shadow-sm leading-relaxed ${
                                    msg.role === 'user' 
                                    ? 'bg-[#213368] text-white rounded-tr-sm' 
                                    : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'
                                }`}>
                                    {msg.text.split('**').map((part, i) => 
                                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                                    )}
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-gray-100 p-3 rounded-2xl rounded-tl-sm shadow-sm">
                                    <div className="flex space-x-1">
                                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-75"></div>
                                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-150"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Input Area */}
                    <form onSubmit={handleSend} className="p-3 bg-white border-t border-gray-100">
                        <div className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-4 py-2 focus-within:ring-2 focus-within:ring-[#20BEC6] focus-within:bg-white transition">
                            <input 
                                ref={inputRef}
                                className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
                                placeholder="Ask a question..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                disabled={isTyping}
                            />
                            {/* Corrected Icon Rotation */}
                            <button 
                                type="submit" 
                                disabled={!input.trim() || isTyping}
                                className="ml-2 text-[#213368] hover:text-[#20BEC6] disabled:opacity-30 transition transform hover:scale-110"
                            >
                                <PaperAirplaneIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="text-center mt-2">
                            <p className="text-[10px] text-gray-400">AI responses are generated based on company docs.</p>
                        </div>
                    </form>
                </div>
            )}

            {/* TOGGLE BUTTON */}
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className={`h-14 w-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                    isOpen ? 'bg-gray-100 text-gray-600 rotate-90' : 'bg-[#20BEC6] text-white'
                }`}
            >
                {isOpen ? <XIcon className="h-6 w-6" /> : <span className="text-2xl font-bold">?</span>}
            </button>
        </div>
    );
}